import React, { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { MeterMark } from './MeterMark';
export function Layout() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isGuidesOpen, setIsGuidesOpen] = useState(false);
  const location = useLocation();
  const guides = [{
    name: 'GitHub Copilot',
    path: '/guides/copilot'
  }, {
    name: 'Cursor',
    path: '/guides/cursor'
  }, {
    name: 'Claude Code',
    path: '/guides/claude-code'
  }, {
    name: 'Windsurf',
    path: '/guides/windsurf'
  }, {
    name: 'Augment Code',
    path: '/guides/augment'
  }, {
    name: 'Gemini Code Assist',
    path: '/guides/gemini'
  }, {
    name: 'OpenAI Codex',
    path: '/guides/codex'
  }];
  const isActive = (path: string) => location.pathname === path;
  const isGuideActive = location.pathname.startsWith('/guides');
  return <div className="min-h-screen flex flex-col bg-slate-950" data-id="element-120">
      {/* Navigation */}
      <header className="sticky top-0 z-50 bg-slate-950/80 backdrop-blur-md border-b border-slate-800" data-id="element-121">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-122">
          <div className="flex justify-between h-16 items-center" data-id="element-123">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 group" data-id="element-124">
              <div className="relative flex items-center justify-center w-9 h-9 bg-slate-900 border border-slate-800 text-slate-500 group-hover:border-emerald-500/50 transition-colors" data-id="element-125">
                <MeterMark size={22} data-id="element-126" />
                <span className="absolute top-1 right-1 w-1 h-1 bg-emerald-500 animate-pulse glow-box-emerald" data-id="element-127"></span>
              </div>
              <span className="font-mono font-bold text-lg tracking-tight text-slate-50" data-id="element-128">
                TokenMeter
              </span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8" data-id="element-129">
              <Link to="/" className="relative text-sm font-medium transition-colors text-slate-400 hover:text-slate-50 py-2" data-id="element-130">
                {isActive('/') && <motion.div layoutId="nav-indicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500 glow-box-emerald" data-id="element-131" />}
                <span className={isActive('/') ? 'text-slate-50' : ''} data-id="element-132">
                  Calculator
                </span>
              </Link>
              <Link to="/compare" className="relative text-sm font-medium transition-colors text-slate-400 hover:text-slate-50 py-2" data-id="element-133">
                {isActive('/compare') && <motion.div layoutId="nav-indicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500 glow-box-emerald" data-id="element-134" />}
                <span className={isActive('/compare') ? 'text-slate-50' : ''} data-id="element-135">
                  Pricing Comparison
                </span>
              </Link>

              {/* Guides Dropdown */}
              <div className="relative py-2" onMouseEnter={() => setIsGuidesOpen(true)} onMouseLeave={() => setIsGuidesOpen(false)} data-id="element-136">
                <button className={`flex items-center gap-1 text-sm font-medium transition-colors ${isGuideActive ? 'text-slate-50' : 'text-slate-400 hover:text-slate-50'}`} data-id="element-137">
                  Cost Guides{' '}
                  <ChevronDown size={16} className={`transition-transform ${isGuidesOpen ? 'rotate-180' : ''}`} data-id="element-138" />
                </button>
                {isGuideActive && !isActive('/') && !isActive('/compare') && !isActive('/track-spend') && <motion.div layoutId="nav-indicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500 glow-box-emerald" data-id="element-139" />}

                <AnimatePresence data-id="element-140">
                  {isGuidesOpen && <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 10
                }} transition={{
                  duration: 0.15
                }} className="absolute top-full left-0 mt-0 w-48 bg-slate-900 border border-slate-800 shadow-2xl rounded-md overflow-hidden py-1" data-id="element-141">
                      {guides.map(guide => <Link key={guide.path} to={guide.path} className={`block px-4 py-2 text-sm ${isActive(guide.path) ? 'bg-slate-800 text-emerald-400 font-medium' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-50'}`} data-id="element-142">
                          {guide.name}
                        </Link>)}
                    </motion.div>}
                </AnimatePresence>
              </div>

              <Link to="/track-spend" className="relative text-sm font-medium transition-colors text-slate-400 hover:text-slate-50 py-2" data-id="element-143">
                {isActive('/track-spend') && <motion.div layoutId="nav-indicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500 glow-box-emerald" data-id="element-144" />}
                <span className={isActive('/track-spend') ? 'text-slate-50' : ''} data-id="element-145">
                  Track AI Spend
                </span>
              </Link>

              <Link to="/faq" className="relative text-sm font-medium transition-colors text-slate-400 hover:text-slate-50 py-2" data-id="element-146">
                {isActive('/faq') && <motion.div layoutId="nav-indicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500 glow-box-emerald" data-id="element-147" />}
                <span className={isActive('/faq') ? 'text-slate-50' : ''} data-id="element-148">
                  FAQ
                </span>
              </Link>
            </nav>

            {/* CTA */}
            <div className="hidden md:flex items-center gap-4" data-id="element-149">
              <a href="https://waydev.co/pricing" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-emerald-500 text-slate-950 px-4 py-2 rounded-md text-sm font-bold hover:bg-emerald-400 transition-colors focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-950 focus:ring-emerald-500 glow-box-emerald" data-id="element-150">
                Get TokenMeter <ExternalLink size={16} data-id="element-151" />
              </a>
            </div>

            {/* Mobile menu button */}
            <button className="md:hidden p-2 text-slate-400 hover:text-slate-50" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu" data-id="element-152">
              {isMobileMenuOpen ? <X size={24} data-id="element-153" /> : <Menu size={24} data-id="element-154" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        <AnimatePresence data-id="element-155">
          {isMobileMenuOpen && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} className="md:hidden border-t border-slate-800 bg-slate-900 overflow-hidden" data-id="element-156">
              <div className="px-4 pt-2 pb-4 space-y-1" data-id="element-157">
                <Link to="/" className="block px-3 py-2 rounded-md text-base font-medium text-slate-300 hover:bg-slate-800 hover:text-slate-50" onClick={() => setIsMobileMenuOpen(false)} data-id="element-158">
                  Calculator
                </Link>
                <Link to="/compare" className="block px-3 py-2 rounded-md text-base font-medium text-slate-300 hover:bg-slate-800 hover:text-slate-50" onClick={() => setIsMobileMenuOpen(false)} data-id="element-159">
                  Pricing Comparison
                </Link>
                <div className="px-3 py-2 text-base font-medium text-slate-50" data-id="element-160">
                  Cost Guides
                </div>
                <div className="pl-6 space-y-1" data-id="element-161">
                  {guides.map(guide => <Link key={guide.path} to={guide.path} className="block px-3 py-2 rounded-md text-sm font-medium text-slate-400 hover:bg-slate-800 hover:text-slate-50" onClick={() => setIsMobileMenuOpen(false)} data-id="element-162">
                      {guide.name}
                    </Link>)}
                </div>
                <Link to="/track-spend" className="block px-3 py-2 rounded-md text-base font-medium text-slate-300 hover:bg-slate-800 hover:text-slate-50" onClick={() => setIsMobileMenuOpen(false)} data-id="element-163">
                  Track AI Spend
                </Link>
                <Link to="/faq" className="block px-3 py-2 rounded-md text-base font-medium text-slate-300 hover:bg-slate-800 hover:text-slate-50" onClick={() => setIsMobileMenuOpen(false)} data-id="element-164">
                  FAQ
                </Link>
                <a href="https://waydev.co/pricing" className="mt-4 block w-full text-center bg-emerald-500 text-slate-950 px-4 py-2 rounded-md text-base font-bold hover:bg-emerald-400 glow-box-emerald" onClick={() => setIsMobileMenuOpen(false)} data-id="element-165">
                  Get TokenMeter
                </a>
              </div>
            </motion.div>}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main className="flex-grow" data-id="element-166">
        <Outlet data-id="element-167" />
      </main>

      {/* Footer */}
      <footer className="bg-slate-950 border-t border-slate-800 py-12 mt-auto" data-id="element-168">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-169">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6" data-id="element-170">
            <div className="flex items-center gap-2.5" data-id="element-171">
              <div className="flex items-center justify-center w-8 h-8 bg-slate-900 border border-slate-800 text-slate-500" data-id="element-172">
                <MeterMark size={20} data-id="element-173" />
              </div>
              <span className="font-mono font-bold text-lg text-slate-50" data-id="element-174">
                TokenMeter
              </span>
            </div>

            <div className="text-sm text-slate-400 flex items-center gap-2" data-id="element-175">
              A free tool by{' '}
              <a href="https://waydev.co" className="font-medium text-slate-300 hover:text-emerald-400 transition-colors" data-id="element-176">
                Waydev, Inc.
              </a>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-slate-500" data-id="element-177">
            <p data-id="element-178">
              © {new Date().getFullYear()} Waydev, Inc. All rights reserved.
              Last updated June 2026.
            </p>
            <div className="flex gap-6" data-id="element-179">
              <Link to="/compare" className="hover:text-slate-300 transition-colors" data-id="element-180">
                Compare Tools
              </Link>
              <Link to="/track-spend" className="hover:text-slate-300 transition-colors" data-id="element-181">
                Track Spend
              </Link>
              <a href="https://waydev.co/privacy" className="hover:text-slate-300 transition-colors" data-id="element-182">
                Privacy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}